// api/services/admin.ts
import { handleRequest } from '@/api/core';

export interface DirectUserPayload {
    name: string;
    email: string;
    password: string;
    entity_type: string;
    entity_id: number;
}

export interface EntityAssociationPayload {
    entity_type: string;
    entity_id: number;
    associated_type: string;
    associated_id: number;
}

export interface ReassignUserPayload {
    entity_type: number;
    entity_id: number;
}

export interface UserReassignResponse {
    message: string;
    user_id: number;
    email_sent: boolean | null;
}

export const createDirectUser = (userData: DirectUserPayload): Promise<unknown> => {
    return handleRequest('post', '/admin/users/direct', userData, 'User created successfully');
};

export const createEntityAssociation = (associationData: EntityAssociationPayload): Promise<unknown> => {
    return handleRequest('post', '/admin/relationships', associationData, 'Entity association created successfully');
};

export const reassignUser = (userId: number, payload: ReassignUserPayload): Promise<UserReassignResponse> =>
    handleRequest<UserReassignResponse>(
        'put',
        `/admin/users/${userId}/reassign`,
        payload,
        'User reassigned. They must sign in again.'
    );

// ===== PARTITIONS =====

export interface Partition {
    id: number;
    name: string;
    entity_id: number;
}

export interface PartitionListResponse {
    partitions: Partition[];
}

const ENTITY_TYPE_INT: Record<string, number> = {
    BROADCASTER: 3,
    ADTECH: 4,
};

export const getEntityPartitions = (entityType: string, entityId: number): Promise<Partition[]> => {
    const typeInt = ENTITY_TYPE_INT[entityType];
    if (!typeInt) return Promise.resolve([]);
    return handleRequest<PartitionListResponse>(
        'get',
        `/partitions/?entity_type=${typeInt}&entity_id=${entityId}`,
        {},
        null,
        false,
        false
    ).then((res) => res.partitions);
};

// ===== REDIS QUEUE MONITORING =====

export const getRedisQueueStats = (): Promise<unknown> =>
    handleRequest('get', '/admin/redis/queue-stats', {}, null, true, false);

export const getRedisQueueSummary = (): Promise<unknown> =>
    handleRequest('get', '/admin/redis/queue-summary', {}, null, true, false);

// ===== DAILY ACTIVITY MONITORING =====

export const getRedisLists = (): Promise<unknown> =>
    handleRequest('get', '/admin/daily-activity', {}, null, true, false);

// ===== REDIS HEALTH & PERFORMANCE MONITORING =====

export const getRedisStrainSummary = (): Promise<unknown> =>
    handleRequest('get', '/admin/redis/strain/summary', {}, null, true, false);

export const getRedisInfo = (): Promise<unknown> => handleRequest('get', '/admin/redis/info', {}, null, true, false);

export const getRedisHealth = (): Promise<unknown> =>
    handleRequest('get', '/admin/redis/health', {}, null, true, false);

// ===== COST TRACKING =====
